import React, { useEffect, useRef, useState } from "react";

const DEFAULT_COOKIE = "azuga_attrib";
const DEFAULT_TTL = 30;

function parseFromUrl(search) {
  const p = new URLSearchParams(search);
  const affiliate_id =
    p.get("affiliate_id") || p.get("aff_id") || p.get("affiliateId") || undefined;
  const offer_id = p.get("offer_id") || p.get("offerId") || undefined;
  const etid =
    p.get("etid") || p.get("transaction_id") || p.get("transactionId") || undefined;
  const result = {};
  if (affiliate_id) result.affiliate_id = affiliate_id;
  if (offer_id) result.offer_id = offer_id;
  if (etid) result.etid = etid;
  return result;
}

function hasKeys(a) {
  return !!a && (!!a.affiliate_id || !!a.offer_id || !!a.etid);
}

function writeCookie(name, value, ttlDays) {
  const expires = new Date();
  expires.setDate(expires.getDate() + ttlDays);
  document.cookie = `${name}=${encodeURIComponent(
    JSON.stringify(value)
  )}; path=/; expires=${expires.toUTCString()}; samesite=Lax`;
}

function readCookie(name) {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.split("=")[1]));
  } catch {
    return null;
  }
}

function headerValue(a) {
  const { affiliate_id, offer_id, etid } = a;
  return JSON.stringify({ affiliate_id, offer_id, etid });
}

async function postAttribution(apiBaseUrl, a) {
  try {
    const base = apiBaseUrl.replace(/\/+$/, "");
    await fetch(`${base}/attribution`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(a),
    });
  } catch {
    // non-blocking
  }
}

export function AzugaAttributionProvider({
  axios,
  apiBaseUrl = import.meta.env.VITE_REACT_APP_API_BASE_URL || "/api/v1/web",
  cookieName = DEFAULT_COOKIE,
  cookieTtlDays = DEFAULT_TTL,
}) {
const location = window.location;
  const [attrib, setAttrib] = useState(null);
  const attribRef = useRef(null);
  attribRef.current = attrib;

  useEffect(() => {
    const urlAttrib = parseFromUrl(location.search);
    if (hasKeys(urlAttrib)) {
      const merged = {
        ...urlAttrib,
        origin_url: window.location.href,
      };
      writeCookie(cookieName, merged, cookieTtlDays);
      try {
        localStorage.setItem(cookieName, JSON.stringify(merged));
      } catch {}
      setAttrib(merged);
      postAttribution(apiBaseUrl, merged);
      return;
    }

    const stored =
      readCookie(cookieName) ||
      (() => {
        try {
          const raw = localStorage.getItem(cookieName);
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();

    setAttrib(hasKeys(stored) ? stored : null);
  }, [location.search, apiBaseUrl, cookieName, cookieTtlDays]);

  useEffect(() => {
    if (!axios) return;
    const id = axios.interceptors.request.use((config) => {
      const a = attribRef.current;
      if (a && hasKeys(a)) {
        const headerVal = headerValue(a);
        const anyConfig = config;
        if (anyConfig.headers && typeof anyConfig.headers.set === "function") {
          anyConfig.headers.set("X-Azuga-Attribution", headerVal);
        } else {
          anyConfig.headers = {
            ...(anyConfig.headers || {}),
            "X-Azuga-Attribution": headerVal,
          };
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [axios]);

  return null;
}

export function withAzugaHeader(init = {}, a) {
  const headers = new Headers(init.headers || {});
  if (a && hasKeys(a)) headers.set("X-Azuga-Attribution", headerValue(a));
  return { ...init, headers, credentials: init.credentials ?? "include" };
}


